import { useState, useEffect, useCallback, useRef } from 'react';
import * as duckdb from '@duckdb/duckdb-wasm';

interface DuckDBState {
  isLoading: boolean;
  isReady: boolean;
  error: string | null;
  connection: duckdb.AsyncDuckDBConnection | null;
}

interface QueryResult<T = any> {
  data: T[];
  isLoading: boolean;
  error: string | null;
}

export const useDuckDB = () => {
  const [state, setState] = useState<DuckDBState>({
    isLoading: true,
    isReady: false,
    error: null,
    connection: null,
  });

  const dbRef = useRef<duckdb.AsyncDuckDB | null>(null);
  const connRef = useRef<duckdb.AsyncDuckDBConnection | null>(null);

  // Initialize DuckDB and load database
  useEffect(() => {
    const initDuckDB = async () => {
      try {
        setState(prev => ({ ...prev, isLoading: true, error: null }));

        // Initialize DuckDB-WASM using local files (no CORS issues)
        const logger = new duckdb.ConsoleLogger(duckdb.LogLevel.WARNING);
        
        // Use local files from public directory with correct paths
        const MANUAL_BUNDLES: duckdb.DuckDBBundles = {
          mvp: {
            mainModule: '/projects/imoveis/duckdb-mvp.wasm',
            mainWorker: '/projects/imoveis/duckdb-browser-mvp.worker.js',
          },
          eh: {
            mainModule: '/projects/imoveis/duckdb-eh.wasm',
            mainWorker: '/projects/imoveis/duckdb-browser-eh.worker.js',
          },
        };

        // Select bundle based on browser capabilities
        const bundle = await duckdb.selectBundle(MANUAL_BUNDLES);
        const worker = new Worker(bundle.mainWorker!);
        
        // Add error handling for worker
        worker.onerror = (error) => {
          console.warn('DuckDB Worker error (non-critical):', error);
        };
        
        const db = new duckdb.AsyncDuckDB(logger, worker);
        
        await db.instantiate(bundle.mainModule, bundle.pthreadWorker);
        dbRef.current = db;

        // Create connection
        const conn = await db.connect();
        connRef.current = conn;

        // H3 extension not needed since Deck.gl handles H3 natively
        // await conn.query('INSTALL h3 FROM community;');
        // await conn.query('LOAD h3;');

        // Load webapp database (simplified - no parquet files needed)
        console.log('Fetching database...');
        const response = await fetch('/projects/imoveis/properties_webapp.duckdb');
        if (!response.ok) {
          throw new Error(`Failed to fetch database: ${response.status}`);
        }
        console.log('Database fetched, converting to array buffer...');
        const arrayBuffer = await response.arrayBuffer();
        const uint8Array = new Uint8Array(arrayBuffer);
        console.log('Registering file buffer...');
        await db.registerFileBuffer('properties_webapp.duckdb', uint8Array);
        console.log('Attaching database...');
        await conn.query("ATTACH 'properties_webapp.duckdb' AS properties_db;");
        console.log('Database attached successfully');

        setState({
          isLoading: false,
          isReady: true,
          error: null,
          connection: conn,
        });

      } catch (error) {
        console.error('Failed to initialize DuckDB:', error);
        setState({
          isLoading: false,
          isReady: false,
          error: error instanceof Error ? error.message : 'Unknown error',
          connection: null,
        });
      }
    };

    initDuckDB();

    // Cleanup
    return () => {
      if (connRef.current) {
        connRef.current.close().catch(console.error);
      }
      if (dbRef.current) {
        dbRef.current.terminate().catch(console.error);
      }
    };
  }, []);

  // Query function
  const query = useCallback(async <T = any>(sql: string): Promise<T[]> => {
    if (!state.connection || !state.isReady) {
      throw new Error('DuckDB is not ready');
    }

    try {
      const result = await state.connection.query(sql);
      return result.toArray().map(row => row.toJSON() as T);
    } catch (error) {
      console.error('Query failed:', sql, error);
      throw error;
    }
  }, [state.connection, state.isReady]);

  // Hook for running queries with loading states
  const useQuery = <T = any>(sql: string | null): QueryResult<T> => {
    const [queryState, setQueryState] = useState<QueryResult<T>>({
      data: [],
      isLoading: false,
      error: null,
    });

    useEffect(() => {
      if (!sql || !state.isReady) return;

      const runQuery = async () => {
        setQueryState(prev => ({ ...prev, isLoading: true, error: null }));
        
        try {
          const data = await query<T>(sql);
          setQueryState({ data, isLoading: false, error: null });
        } catch (error) {
          setQueryState({
            data: [],
            isLoading: false,
            error: error instanceof Error ? error.message : 'Query failed',
          });
        }
      };

      runQuery();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sql, state.isReady]);

    return queryState;
  };

  return {
    isLoading: state.isLoading,
    isReady: state.isReady,
    error: state.error,
    query,
    useQuery,
  };
};
